import { useMemo, useState } from "react";

import { filterItemsBySearchTerm } from "utils/searchUtils";

const resolveFilterFn = (searchableFieldsOrFilterFn) => {
  if (!searchableFieldsOrFilterFn) {
    return null;
  }

  if (typeof searchableFieldsOrFilterFn === "function") {
    return searchableFieldsOrFilterFn;
  }

  if (Array.isArray(searchableFieldsOrFilterFn)) {
    return (items, searchTerm) =>
      filterItemsBySearchTerm(items, searchTerm, searchableFieldsOrFilterFn);
  }

  return null;
};

const usePaginatedList = (items, pageSize, searchableFieldsOrFilterFn) => {
  const filterFn = useMemo(
    () => resolveFilterFn(searchableFieldsOrFilterFn),
    [searchableFieldsOrFilterFn]
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const filteredItems = useMemo(() => {
    if (filterFn) {
      return filterFn(items, searchTerm);
    }
    return items;
  }, [items, searchTerm, filterFn]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));

  const paginatedItems = useMemo(
    () =>
      filteredItems.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
      ),
    [filteredItems, currentPage, pageSize]
  );

  const handleSearchTermChange = (nextSearchTerm) => {
    setSearchTerm(nextSearchTerm);
    setCurrentPage(1);
  };

  const handlePageChange = (nextPage) => {
    setCurrentPage(nextPage);
  };

  return {
    searchTerm,
    setSearchTerm: handleSearchTermChange,
    currentPage,
    setCurrentPage: handlePageChange,
    filteredItems,
    paginatedItems,
    totalPages,
  };
};

export default usePaginatedList;
